import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

const FORM_ID = 'db7mf7itp9sllopf7hqg';
const SUBMIT_ADDR = 'https://readdy.ai/api/form/db7mf7itp9sllopf7hqg';

const FEATURES = [
  { icon: 'ri-moon-clear-line', title: '只做午夜', desc: '内容围绕“深夜适合观看”的主题，拒绝堆量，专注氛围。' },
  { icon: 'ri-shield-star-line', title: '认真整理', desc: '每部影片都有简介、标签与午夜看点，帮你在看之前做判断。' },
  { icon: 'ri-user-heart-line', title: '为影迷而做', desc: '由喜爱悬疑、恐怖与剧情片的影迷共同维护的片单。' },
];

export default function About() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useDocumentTitle({
    title: '关于我们 - 午夜小电影',
    description:
      '午夜小电影是一个专注精选午夜主题短片与电影的影迷网站，介绍我们的定位、内容范围、联系方式与反馈渠道。',
    keywords: '午夜小电影,关于午夜小电影,午夜影视推荐,午夜电影网站',
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;

    const data = new FormData(form);
    const honeypot = String(data.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      return;
    }
    data.delete('website_alt');

    const email = String(data.get('email') || '').trim();
    if (!email) {
      setFormError('请填写你的邮箱，方便我们回复。');
      setStatus('error');
      return;
    }

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(data as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText || '';

      const isSpam = /spam/i.test(serverMsg);
      const ok = response.ok && parsed?.code === 'OK' && !isSpam;

      if (ok) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(isSpam ? '提交被拦截，请稍后重试。' : serverMsg || '提交失败，请稍后重试。');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请稍后重试。');
    }
  };

  return (
    <div className="mx-auto w-full px-4 py-8 md:px-8 md:py-12">
      <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
        <Link to="/" className="transition hover:text-primary-400">首页</Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">关于我们</span>
      </nav>

      <header className="mt-6 max-w-3xl">
        <p className="font-display text-sm tracking-[0.25em] text-primary-400">ABOUT US</p>
        <h1 className="mt-2 text-3xl text-foreground-950 md:text-4xl">关于午夜小电影</h1>
        <p className="mt-4 text-sm leading-loose text-foreground-700 md:text-base">
          「午夜小电影」是一个专注精选午夜主题短片与电影的影迷网站。我们相信，
          深夜看片是一种特别的仪式感——那时候的世界安静下来，故事会变得格外清晰。
          因此我们把内容聚焦在<strong className="text-foreground-950">剧情、悬疑、恐怖</strong>
          等适合午夜观看的类型上，帮你更轻松地发现、筛选并了解一部作品。
        </p>
        <p className="mt-3 text-sm leading-loose text-foreground-700 md:text-base">
          我们不提供在线播放与下载，只做影片资讯与观看前参考。希望当你在凌晨两点打开网站时，
          这里能让你少一点选择困难，多一分沉浸。
        </p>
      </header>

      <section className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
        {FEATURES.map((item) => (
          <div key={item.title} className="rounded-lg border border-background-300/50 bg-background-100 p-6">
            <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-500/15 text-primary-400">
              <i className={`${item.icon} text-xl`}></i>
            </span>
            <h2 className="mt-5 text-base text-foreground-950">{item.title}</h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
          </div>
        ))}
      </section>

      <section className="mt-14 grid grid-cols-1 gap-8 lg:grid-cols-5">
        <div className="lg:col-span-2">
          <h2 className="text-xl text-foreground-950">联系我们</h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600">
            有任何影片推荐、内容纠错或合作想法，都欢迎告诉我们。我们会尽快查看并回复。
          </p>

          <div className="mt-6 flex flex-col gap-4">
            <div className="flex items-center gap-3 rounded-lg border border-background-300/50 bg-background-100 p-4">
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-500/15 text-accent-500">
                <i className="ri-mail-line text-lg"></i>
              </span>
              <div>
                <p className="text-xs text-foreground-500">邮箱</p>
                <p className="text-sm text-foreground-900">hello@midnight-cinema.example</p>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-lg border border-background-300/50 bg-background-100 p-4">
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-500/15 text-accent-500">
                <i className="ri-time-line text-lg"></i>
              </span>
              <div>
                <p className="text-xs text-foreground-500">回复时间</p>
                <p className="text-sm text-foreground-900">工作日 10:00 - 20:00（示例）</p>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-3">
          <div className="rounded-lg border border-background-300/50 bg-background-100 p-6 md:p-8">
            <h2 className="text-xl text-foreground-950">反馈与建议</h2>
            <p className="mt-2 text-sm text-foreground-600">填写下面的表单，把你的想法留给我们。</p>

            {status === 'success' ? (
              <div className="mt-6 flex flex-col items-center rounded-lg border border-primary-500/30 bg-primary-500/10 py-12 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-500 text-white">
                  <i className="ri-check-line text-2xl"></i>
                </span>
                <h3 className="mt-4 text-lg text-foreground-950">提交成功，感谢你的反馈</h3>
                <p className="mt-2 text-sm text-foreground-600">我们会认真阅读每一条建议。</p>
                <button
                  type="button"
                  onClick={() => setStatus('idle')}
                  className="mt-6 flex h-11 items-center gap-2 whitespace-nowrap rounded-md border border-background-300/70 px-6 text-sm text-foreground-700 transition hover:text-foreground-950"
                >
                  再写一条
                </button>
              </div>
            ) : (
              <form
                id={FORM_ID}
                data-readdy-form
                onSubmit={handleSubmit}
                className="mt-6 flex flex-col gap-5"
                noValidate
              >
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <div className="flex flex-col gap-2">
                    <label htmlFor="name" className="text-sm text-foreground-700">昵称</label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="怎么称呼你？"
                      className="h-11 rounded-md border border-background-300/60 bg-background-50 px-4 text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label htmlFor="email" className="text-sm text-foreground-700">邮箱 <span className="text-primary-400">*</span></label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      placeholder="name@example.com"
                      className="h-11 rounded-md border border-background-300/60 bg-background-50 px-4 text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="topic" className="text-sm text-foreground-700">反馈类型</label>
                  <select
                    id="topic"
                    name="topic"
                    defaultValue="内容建议"
                    className="h-11 rounded-md border border-background-300/60 bg-background-50 px-4 text-sm text-foreground-950 focus:border-primary-400 focus:outline-none"
                  >
                    <option value="内容建议">内容建议</option>
                    <option value="影片推荐">影片推荐</option>
                    <option value="内容纠错">内容纠错</option>
                    <option value="合作洽谈">合作洽谈</option>
                  </select>
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="message" className="text-sm text-foreground-700">留言内容</label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    maxLength={500}
                    placeholder="最多 500 字，告诉我们你的想法…"
                    className="resize-none rounded-md border border-background-300/60 bg-background-50 px-4 py-3 text-sm leading-relaxed text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                  ></textarea>
                </div>

                <div className="email-confirm-guard" aria-hidden="true">
                  <label htmlFor="website_alt">请勿填写此栏</label>
                  <input
                    id="website_alt"
                    name="website_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    readOnly
                  />
                </div>

                {status === 'error' && formError && (
                  <div className="flex items-center gap-2 rounded-md border border-primary-500/40 bg-primary-500/10 px-4 py-3 text-sm text-foreground-800">
                    <i className="ri-error-warning-line text-primary-400"></i>
                    {formError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="flex h-12 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 text-sm font-medium text-white transition hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {status === 'submitting' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>提交中…
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line"></i>提交反馈
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <section id="privacy" className="mt-14 rounded-lg border border-background-300/50 bg-background-100 p-6 md:p-8">
        <h2 className="text-xl text-foreground-950">使用说明与隐私政策</h2>
        <div className="mt-4 grid grid-cols-1 gap-6 text-sm leading-relaxed text-foreground-600 md:grid-cols-2">
          <div>
            <h3 className="text-base text-foreground-900">使用说明</h3>
            <ul className="mt-3 flex flex-col gap-2.5">
              <li className="flex gap-2"><i className="ri-checkbox-circle-line mt-0.5 text-primary-400"></i>首页焦点大图可直接进入影片详情，或通过分类入口快速浏览同类型影片。</li>
              <li className="flex gap-2"><i className="ri-checkbox-circle-line mt-0.5 text-primary-400"></i>分类页支持按年份、评分、时长筛选，并可按最新、热门、评分排序。</li>
              <li className="flex gap-2"><i className="ri-checkbox-circle-line mt-0.5 text-primary-400"></i>收藏会保存在你的浏览器本地，清除浏览器数据后收藏将消失。</li>
            </ul>
          </div>
          <div>
            <h3 className="text-base text-foreground-900">隐私说明</h3>
            <ul className="mt-3 flex flex-col gap-2.5">
              <li className="flex gap-2"><i className="ri-shield-check-line mt-0.5 text-accent-500"></i>收藏记录仅存储在你自己的设备上，我们不会收集或上传。</li>
              <li className="flex gap-2"><i className="ri-shield-check-line mt-0.5 text-accent-500"></i>反馈表单中你填写的邮箱仅用于回复本次反馈，不会用于其他用途。</li>
              <li className="flex gap-2"><i className="ri-shield-check-line mt-0.5 text-accent-500"></i>本站内容为影视资讯演示，不含在线播放与下载功能。</li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}